import { useState, type FormEvent, type ReactNode } from "react";
import { can, scopeFor, type TaskPriority } from "@sts/schemas";
import type { Me, Task, TaskCounts, UpdateTaskInput } from "../api/types.js";
import { ApiError, ApiUnreachableError, refusedAction } from "../api/client.js";
import { Async, Failure } from "../components/Async.jsx";
import { PriorityBadge, TaskState } from "../components/Priority.jsx";
import { usePortal, useSession } from "../app/context.js";
import { useAsync } from "../app/use-async.js";
import { useSubmit } from "../app/use-submit.js";
import { href } from "../app/router.js";
import { isoDay } from "../lib/format.js";
import { resolveJobReferences } from "../lib/job-references.js";
import {
  PRIORITY_LABEL,
  PRIORITY_ORDER,
  assigneeLabel,
  isOverdue,
  tally,
  todayUtc,
} from "../lib/tasks.js";

/**
 * The task inbox: mine, or the whole organisation's.
 *
 * The order is the API's and is not touched here. `crm/tasks.ts` sorts urgent
 * before high before normal and, within a priority, soonest due first with
 * undated work last — which already lifts what is late to the top of its band,
 * because an overdue task is by definition the one with the earliest due date.
 * A portal that re-sorted would disagree with `GET /api/tasks` about what needs
 * attention, and there is no version of that disagreement where the portal wins.
 *
 * What this screen adds is legibility. Priority is shown as a word and a mark,
 * never as a colour alone — the board is printed in black and white for site
 * meetings and read every day by people who cannot tell the amber row from the
 * red one. "Overdue" is a word for the same reason.
 *
 * The two scopes are two different routes rather than a filter over one list:
 * `?mine=true` is what the permission matrix intends a field role to use, and it
 * is the only one such a role is allowed at all. Where the role holds nothing
 * wider, the toggle is not offered — a control that can only ever be refused is
 * worse than its absence.
 */

type Scope = "mine" | "organisation";

interface Inbox {
  readonly me: Me;
  readonly scope: Scope;
  /** Whether this role may see beyond its own work at all. */
  readonly mayViewOrganisation: boolean;
  readonly tasks: readonly Task[];
  /** The organisation's own tally, or null when the role may not have it. */
  readonly counts: TaskCounts | null;
  readonly references: ReadonlyMap<string, string>;
  readonly notes: readonly string[];
}

export function Tasks(): ReactNode {
  const { client, known } = usePortal();
  const session = useSession();
  const organisationId = session?.organisationId ?? null;
  const knownJobs = known.list("job", organisationId);
  const knownKey = knownJobs.join(",");
  const [chosen, setChosen] = useState<Scope | null>(null);

  const { state, reload } = useAsync<Inbox>(async () => {
    const notes: string[] = [];
    const me = await client.me();
    const mayViewOrganisation =
      scopeFor(me.role, "task", "read") === "org";

    // The default is the widest view the role actually holds, so a surveyor
    // lands on their own work rather than on a refusal.
    const scope: Scope =
      chosen ?? (mayViewOrganisation ? "organisation" : "mine");

    const tasks = await client.tasks({ mine: scope === "mine" });

    const [countResult, referenceResult] = await Promise.allSettled([
      client.taskCounts(),
      // The tasks' own jobs and the ones this browser remembers, together: the
      // first names the column, the second names the options in "attach to a
      // job", and a uuid in a dropdown is not a choice anybody can make.
      resolveJobReferences(client, [
        ...tasks
          .map((task) => task.job_id)
          .filter((id): id is string => id !== null),
        ...knownJobs,
      ]),
    ]);

    let counts: TaskCounts | null = null;
    if (countResult.status === "fulfilled") {
      counts = countResult.value;
    } else {
      notes.push(describe("the organisation's totals", countResult.reason));
    }

    const references =
      referenceResult.status === "fulfilled"
        ? referenceResult.value.references
        : new Map<string, string>();
    if (
      referenceResult.status === "fulfilled" &&
      referenceResult.value.unresolved > 0
    ) {
      const n = referenceResult.value.unresolved;
      // Deliberately not "tasks refer to jobs you cannot read": the ids here are
      // the tasks' jobs AND the ones this browser remembers, and a remembered id
      // may simply be stale. Saying only what is known to be true.
      notes.push(
        `${n} ${n === 1 ? "job" : "jobs"} referred to here could not be read — ` +
          `gone, or not yours — so ${n === 1 ? "it is" : "they are"} shown by id.`,
      );
    }

    return {
      me,
      scope,
      mayViewOrganisation,
      tasks,
      counts,
      references,
      notes,
    };
  }, [client, chosen, knownKey]);

  return (
    <>
      <div className="page-header spread">
        <h1>Tasks</h1>
        <button className="secondary" onClick={reload}>
          Refresh
        </button>
      </div>

      <Async state={state} what="the task inbox" reload={reload}>
        {(inbox) => (
          <Board
            inbox={inbox}
            onScope={setChosen}
            reload={reload}
            knownJobs={knownJobs}
          />
        )}
      </Async>
    </>
  );
}

function describe(what: string, error: unknown): string {
  if (error instanceof ApiError) {
    const refused = refusedAction(error);
    return refused
      ? `${what} are not shown: your role may not ${refused.action} ${refused.resource} across the organisation.`
      : `${what} could not be loaded: ${error.message}`;
  }
  if (error instanceof ApiUnreachableError) {
    return `${what} could not be reached.`;
  }
  return `${what} could not be loaded.`;
}

function Board({
  inbox,
  onScope,
  reload,
  knownJobs,
}: {
  readonly inbox: Inbox;
  readonly onScope: (scope: Scope) => void;
  readonly reload: () => void;
  readonly knownJobs: readonly string[];
}): ReactNode {
  const today = todayUtc();
  const counted = tally(inbox.tasks, today);

  return (
    <>
      {inbox.mayViewOrganisation ? (
        <fieldset className="scope">
          <legend>Whose work</legend>
          <label htmlFor="scope-mine">
            <input
              id="scope-mine"
              type="radio"
              name="scope"
              checked={inbox.scope === "mine"}
              onChange={() => onScope("mine")}
            />{" "}
            Mine
          </label>
          <label htmlFor="scope-organisation">
            <input
              id="scope-organisation"
              type="radio"
              name="scope"
              checked={inbox.scope === "organisation"}
              onChange={() => onScope("organisation")}
            />{" "}
            The whole organisation
          </label>
        </fieldset>
      ) : (
        <p className="muted small">
          Your role is scoped to the records you are assigned to, so this is your
          own work. There is no organisation-wide view to switch to.
        </p>
      )}

      <p className="tally-line">
        <span className="count">{counted.open} open</span>{" "}
        <span className="count">{counted.urgent} urgent</span>{" "}
        <span className={counted.overdue > 0 ? "count status-breached" : "count"}>
          {counted.overdue} overdue
        </span>
        {inbox.counts ? (
          <span className="muted small">
            {" "}
            · across the organisation: {inbox.counts.urgent} urgent,{" "}
            {inbox.counts.high} high, {inbox.counts.normal} normal,{" "}
            {inbox.counts.overdue} overdue
          </span>
        ) : null}
      </p>

      {inbox.notes.length > 0 ? (
        <div className="notice" role="note">
          <ul className="small">
            {inbox.notes.map((note) => (
              <li key={note}>{note}</li>
            ))}
          </ul>
        </div>
      ) : null}

      {inbox.tasks.length === 0 ? (
        <p className="empty">
          Nothing open{inbox.scope === "mine" ? " assigned to you" : ""}. Only
          open tasks are listed; completing one takes it off this board rather
          than greying it out.
        </p>
      ) : (
        <div className="table-scroll">
          <table>
            <caption className="visually-hidden">
              Open tasks, most urgent first, then soonest due
            </caption>
            <thead>
              <tr>
                <th scope="col">Priority</th>
                <th scope="col">Task</th>
                <th scope="col">Job</th>
                <th scope="col">Assigned</th>
                <th scope="col">Due</th>
                <th scope="col">State</th>
                <th scope="col">Do</th>
              </tr>
            </thead>
            <tbody>
              {inbox.tasks.map((task) => (
                <Row
                  key={task.id}
                  task={task}
                  me={inbox.me}
                  today={today}
                  reference={
                    task.job_id ? (inbox.references.get(task.job_id) ?? null) : null
                  }
                  reload={reload}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}

      <NewTask
        me={inbox.me}
        knownJobs={knownJobs}
        references={inbox.references}
        reload={reload}
      />
    </>
  );
}

function Row({
  task,
  me,
  today,
  reference,
  reload,
}: {
  readonly task: Task;
  readonly me: Me;
  readonly today: string;
  readonly reference: string | null;
  readonly reload: () => void;
}): ReactNode {
  const { client } = usePortal();
  const { busy, error, submit } = useSubmit();

  const overdue = isOverdue(task, today);
  const assignee = assigneeLabel(task, me.userId);

  // Asked of the same matrix the API enforces. A field role holds `task:update`
  // at "own" scope, so the controls appear on their own tasks and not on a
  // colleague's — rather than appearing everywhere and failing on click.
  const mayUpdate = can({
    role: me.role,
    resource: "task",
    action: "update",
    isAssigned: task.assigned_to_user_id === me.userId,
  });

  const change = (input: UpdateTaskInput) => () =>
    void submit(async () => {
      await client.updateTask(task.id, input);
      reload();
    });

  return (
    <>
      <tr className={overdue ? "urgency-breached" : undefined}>
        <td>
          <PriorityBadge priority={task.priority} />
        </td>
        <th scope="row">{task.title}</th>
        <td>
          {task.job_id === null ? (
            <span className="muted">Not attached</span>
          ) : (
            <a href={href.job(task.job_id)}>
              {reference ?? <span className="mono small">{task.job_id}</span>}
            </a>
          )}
        </td>
        <td className={assignee.isMine ? undefined : "muted"}>{assignee.text}</td>
        <td className="ts">
          {task.due_at ? isoDay(task.due_at) : <span className="muted">None</span>}
        </td>
        <td>
          <TaskState overdue={overdue} done={task.completed_at !== null} />
        </td>
        <td>
          {mayUpdate ? (
            <span className="row-actions">
              <label
                className="visually-hidden"
                htmlFor={`priority-${task.id}`}
              >
                Priority for {task.title}
              </label>
              <select
                id={`priority-${task.id}`}
                value={task.priority}
                disabled={busy}
                onChange={(e) => {
                  // Read before the await, not inside it: the value belongs to
                  // the event, and reaching back into `e.target` after an
                  // asynchronous boundary is a habit that works until the day
                  // the element has been re-rendered underneath it.
                  const priority = e.target.value as TaskPriority;
                  void submit(async () => {
                    await client.updateTask(task.id, { priority });
                    reload();
                  });
                }}
              >
                {PRIORITY_ORDER.map((priority) => (
                  <option key={priority} value={priority}>
                    {PRIORITY_LABEL[priority]}
                  </option>
                ))}
              </select>{" "}
              {assignee.isMine ? (
                <button
                  className="link"
                  disabled={busy}
                  onClick={change({ assignedToUserId: null })}
                >
                  Release
                </button>
              ) : (
                <button
                  className="link"
                  disabled={busy}
                  onClick={change({ assignedToUserId: me.userId })}
                >
                  Take
                </button>
              )}{" "}
              <button
                className="link"
                disabled={busy}
                onClick={change({ completed: true })}
              >
                Done
              </button>
            </span>
          ) : (
            <span className="muted small">Not yours to change</span>
          )}
        </td>
      </tr>
      {error !== null ? (
        <tr>
          <td colSpan={7}>
            <Failure error={error} what={`the change to "${task.title}"`} />
          </td>
        </tr>
      ) : null}
    </>
  );
}

function NewTask({
  me,
  knownJobs,
  references,
  reload,
}: {
  readonly me: Me;
  readonly knownJobs: readonly string[];
  readonly references: ReadonlyMap<string, string>;
  readonly reload: () => void;
}): ReactNode {
  const { client } = usePortal();
  const { busy, error, submit } = useSubmit();
  const [title, setTitle] = useState("");
  const [priority, setPriority] = useState<TaskPriority>("normal");
  const [dueOn, setDueOn] = useState("");
  const [jobId, setJobId] = useState("");
  const [mine, setMine] = useState(true);

  if (!can({ role: me.role, resource: "task", action: "create" })) {
    return (
      <div className="notice" role="note">
        <p className="small">
          Your role may not create tasks, so the form for a new one is not shown.
        </p>
      </div>
    );
  }

  async function onSubmit(event: FormEvent): Promise<void> {
    event.preventDefault();
    await submit(async () => {
      await client.createTask({
        title: title.trim(),
        priority,
        ...(jobId ? { jobId } : {}),
        ...(mine ? { assignedToUserId: me.userId } : {}),
        // Sent as an explicit instant rather than a bare date. The column is a
        // `timestamptz`, so a bare "2026-09-01" would be read in whatever
        // timezone the server session happens to hold.
        ...(dueOn ? { dueAt: `${dueOn}T00:00:00.000Z` } : {}),
      });
      setTitle("");
      setPriority("normal");
      setDueOn("");
      setJobId("");
      reload();
    });
  }

  return (
    <section>
      <h2>Add a task</h2>
      <form className="stacked" onSubmit={(e) => void onSubmit(e)}>
        <div className="field">
          <label htmlFor="task-title">What needs doing</label>
          <input
            id="task-title"
            type="text"
            required
            value={title}
            onChange={(e) => setTitle(e.target.value)}
          />
        </div>
        <div className="field">
          <label htmlFor="task-priority">Priority</label>
          <select
            id="task-priority"
            value={priority}
            onChange={(e) => setPriority(e.target.value as TaskPriority)}
          >
            {PRIORITY_ORDER.map((option) => (
              <option key={option} value={option}>
                {PRIORITY_LABEL[option]}
              </option>
            ))}
          </select>
        </div>
        <div className="field">
          <label htmlFor="task-due">Due</label>
          <input
            id="task-due"
            type="date"
            value={dueOn}
            onChange={(e) => setDueOn(e.target.value)}
          />
        </div>
        <div className="field">
          <label htmlFor="task-job">Job</label>
          <select
            id="task-job"
            value={jobId}
            onChange={(e) => setJobId(e.target.value)}
          >
            <option value="">Not attached to a job</option>
            {knownJobs.map((id) => (
              <option key={id} value={id}>
                {references.get(id) ?? id}
              </option>
            ))}
          </select>
          <p className="hint small">
            Only jobs this browser has opened are listed here. `GET /api/jobs`
            exists now (see the Pipeline board) — open the job there first and
            it will appear in this list too.
          </p>
        </div>
        <div className="field">
          <label htmlFor="task-mine">
            <input
              id="task-mine"
              type="checkbox"
              checked={mine}
              onChange={(e) => setMine(e.target.checked)}
            />{" "}
            Assign it to me
          </label>
          <p className="hint small">
            The API takes a user id and the portal knows only its own, so a task
            can be put in your hands or left unassigned for somebody to take.
          </p>
        </div>
        <button type="submit" disabled={busy || title.trim() === ""}>
          {busy ? "Saving…" : "Add task"}
        </button>
      </form>
      {error !== null ? <Failure error={error} what="that task" /> : null}
    </section>
  );
}
